// Espace du partenaire (D-033) : /p/<jeton>, lien privé envoyé par le club, sans
// compte. Sa visibilité (affichages, clics), ses emplacements, ses matchs présentés,
// son offre aux membres, et de quoi soutenir le club. Aucune donnée personnelle.
// L'app offerte par un partenaire (D-147) : proposée d'office tant qu'aucun partenaire ne l'offre, la promesse de don.
// Avantages validés par QR code (D-156) : le lien ouvert ici est gardé sur ce téléphone (le scan d'un QR de membre le
// reconnaît ensuite) ; « Valider un avantage » (« Taper un code ») en tête, ses validations sous sa visibilité.
import { partnerAnswerAsk, partnerAsks, partnerReport, type PartnerAsk, type PartnerReport } from '../lib/api';
import type { AppOffer } from '../lib/lien-api';
import { renderPartnerSpaceReceipts } from './recus';
import { clicks, KIND_LABEL, PLACEMENT_LABEL, views, type Placement } from '../domain/partners';
import { $$, html, mount } from './dom';
import { partnerMark } from './partners';
import { applyBrand } from './theme';
import { loadOffer, offerCard, wireOffer } from './official-partner';
import { partnerValidations, rememberPartnerLink, type PartnerValidations } from '../lib/perk-qr-api';
import { clubOrigin } from '../domain/links';
import { openCodeSheet, partnerValidationsBlock, validateCard } from './perk-scan';

export async function renderPartnerReport(root: HTMLElement, token: string): Promise<void> {
  // Lien privé : pas d'indexation par les moteurs de recherche.
  const robots = document.createElement('meta');
  robots.name = 'robots'; robots.content = 'noindex, nofollow';
  document.head.append(robots);

  let r: PartnerReport | null = null;
  let asks: PartnerAsk[] = [];
  let official: AppOffer | null = null;
  let qv: PartnerValidations | null = null;
  try {
    [r, asks, official, qv] = await Promise.all([partnerReport(token), partnerAsks(token).catch(() => [] as PartnerAsk[]), loadOffer(token),
      partnerValidations(null, token).catch(() => null)]);
  } catch { /* même message */ }
  if (!r) {
    document.title = 'Lien invalide';
    mount(root, html`
      <main class="pub-missing">
        <h1>Lien invalide</h1>
        <p>Ce lien d'espace partenaire n'existe pas ou a été renouvelé par le club. Demandez-lui le nouveau.</p>
      </main>`);
    return;
  }
  // D-156 : le lien privé s'ouvre sur l'adresse du club, celle des QR des membres (D-104) ; gardé sur ce téléphone, il le fait
  // reconnaître au prochain scan.
  const home = clubOrigin(qv?.club_slug, import.meta.env.VITE_CLUB_HOST_BASE as string | undefined, location);
  if (home.onClubHost && home.origin !== location.origin) { location.replace(`${home.origin}/p/${token}`); return; }
  rememberPartnerLink(token);
  const { club, partner, month, season, matches } = r;
  applyBrand({ id: 'partner-report', name: club.name, short_name: club.short_name, logo_url: club.logo, primary_color: club.color }, false);
  document.title = `${partner.name} · ${club.name}`;
  const tz = club.timezone || 'Europe/Paris';
  const fmt = (iso: string, o: Intl.DateTimeFormatOptions) => new Intl.DateTimeFormat('fr-FR', { timeZone: tz, ...o }).format(new Date(iso));
  const monthName = fmt(new Date().toISOString(), { month: 'long' });
  const nowIso = new Date().toISOString();
  const bannerOn = !!partner.banner_until && partner.banner_until >= nowIso.slice(0, 10);
  const places = (Object.keys(season) as Placement[]).filter(k => (season[k]?.views ?? 0) + (season[k]?.clicks ?? 0) > 0);
  const officialOn = official?.state === 'mine';
  const live = [partner.show_public, bannerOn, matches.some(m => m.starts_at >= nowIso), !!partner.member_offer, officialOn].filter(Boolean).length;
  const sinceLine = [partner.level ? `Partenaire ${partner.level}` : KIND_LABEL[partner.kind], partner.since_year ? `depuis ${partner.since_year}` : ''].filter(Boolean).join(' · ');

  mount(root, html`
    <div class="pr">
      <header class="pr-head">
        ${club.logo ? html`<img class="pr-club" src="${club.logo}" alt="">` : ''}
        <div><strong>${club.name}</strong><span class="pr-kicker">Espace partenaire</span></div>
      </header>
      <main class="pub-main">
        <div id="pr-qx"></div>
        <div class="pr-id">
          ${partnerMark({ name: partner.name, logo: partner.logo }, 'pt-logo pt-logo-lg')}
          <div><h1>${partner.name}</h1><p class="muted">${sinceLine}</p></div>
        </div>
        ${partner.restricted ? html`<p class="card small">Votre partenariat n'est affiché nulle part (alcool ou jeux d'argent : loi Évin). Merci pour votre soutien !</p>` : ''}
        ${partner.restricted ? '' : offerCard(official, { name: partner.name, logo: partner.logo }, club.name, null)}

        <section class="card pr-kpis" aria-label="Votre visibilité">
          <p class="pub-kicker">Votre visibilité · ${monthName}</p>
          <div class="lic-kpis">
            <span><b>${month.views.toLocaleString('fr-FR')}</b><small>affichages</small></span>
            <span><b>${month.clicks.toLocaleString('fr-FR')}</b><small>clics</small></span>
            <span><b>${live}</b><small>emplacements actifs</small></span>
          </div>
          ${places.length ? html`<p class="muted small">Depuis le début de la saison : ${places.map(k => `${PLACEMENT_LABEL[k]} ${views(season[k]!.views)}, ${clicks(season[k]!.clicks)}`).join(' · ')}.</p>` : ''}
          <p class="hint">Mesure : nombre d'affichages et de clics, sans aucun suivi des personnes.</p>
        </section>

        <div id="pr-qxv"></div>
        ${asks.map(a => html`
          <section class="pr-ask">
            <p class="pub-kicker">Le club a besoin de vous · ${fmt(a.starts_at, { day: 'numeric', month: 'short' })}</p>
            <h2>${a.event} : ${a.label}</h2>
            ${a.counterpart ? html`<p>En échange : ${a.counterpart}</p>` : ''}
            ${a.mine ? html`<p class="pr-ask-state">${a.status === 'accepte' ? 'Merci : votre participation est confirmée.' : 'Proposé : le club vous recontacte pour confirmer.'}</p>`
              : html`<button type="button" class="btn pr-ask-btn" data-ask="${a.id}">Je participe</button>`}
          </section>`)}

        <section class="pub-sec">
          <h2>Vos emplacements</h2>
          <ul class="pub-list pr-places">
            ${officialOn ? html`<li><span class="pub-what"><strong>Partenaire officiel de l’app</strong><small>Saison ${official!.season} · accueil de chaque membre, page publique</small></span>
              <span class="chip chip-ok">Actif</span></li>` : ''}
            <li><span class="pub-what"><strong>Page publique du club</strong><small>« Ils nous soutiennent »</small></span>
              <span class="chip ${partner.show_public ? 'chip-ok' : 'chip-mute'}">${partner.show_public ? 'Actif' : 'Non'}</span></li>
            <li><span class="pub-what"><strong>Bannière d'accueil de l'app</strong><small>${bannerOn ? `Jusqu'au ${fmt(`${partner.banner_until}T12:00:00Z`, { day: 'numeric', month: 'long' })}` : 'Aux membres du club, à tour de rôle'}</small></span>
              <span class="chip ${bannerOn ? 'chip-ok' : 'chip-mute'}">${bannerOn ? 'Active' : 'Non'}</span></li>
            ${partner.member_offer ? html`<li><span class="pub-what"><strong>Offre membres : ${partner.member_offer}</strong><small>Visible des licenciés et des familles</small></span><span class="chip chip-ok">Active</span></li>` : ''}
            ${asks.filter(a => a.mine && a.status === 'accepte').map(a => html`<li><span class="pub-what"><strong>Événement : ${a.event}</strong><small>${a.label} · ${fmt(a.starts_at, { weekday: 'short', day: 'numeric', month: 'short' })}</small></span><span class="chip chip-ok">Confirmé</span></li>`)}
            ${matches.map(m => html`<li><span class="pub-what"><strong>« Match présenté par »</strong><small>${m.team} – ${m.opponent} · ${fmt(m.starts_at, { weekday: 'short', day: 'numeric', month: 'short' })}${m.score_us != null && !(m.score_us === 0 && m.score_them === 0) ? ` · ${m.score_us}–${m.score_them}` : ''}</small></span>
              <span class="chip ${m.starts_at >= nowIso ? 'chip-ok' : 'chip-mute'}">${m.starts_at >= nowIso ? 'À venir' : 'Joué'}</span></li>`)}
          </ul>
        </section>

        <div id="pr-recus"></div>
        <section class="pr-actions">
          ${club.donation_url ? html`<a class="btn btn-primary" href="${club.donation_url}" target="_blank" rel="noopener">Faire un don au club</a>` : ''}
          ${club.public ? html`<a class="btn btn-ghost" href="/c/${club.public}" target="_blank" rel="noopener">Voir la page du club</a>` : ''}
          ${club.email ? html`<p class="small">Une question, un nouvel emplacement ? <a href="mailto:${club.email}">${club.email}</a></p>` : ''}
        </section>
      </main>
      <footer class="pub-foot"><div><strong>${club.name}</strong><br><small>Espace partenaire · propulsé par Arceau</small></div></footer>
    </div>`);
  wireOffer(root, token, official, partner, club.name, () => void renderPartnerReport(root, token));
  void renderPartnerSpaceReceipts(root.querySelector<HTMLElement>('#pr-recus')!, token);   // ses reçus fiscaux (D-124)
  void Promise.resolve(qv).then(v => {   // D-156 : « Valider un avantage », ses validations
    if (!v) return;
    const card = root.querySelector<HTMLElement>('#pr-qx'), list = root.querySelector<HTMLElement>('#pr-qxv');
    if (card && v.perks.some(k => k.active)) {
      mount(card, validateCard(v, null));
      card.querySelector('[data-qx-code]')?.addEventListener('click', () => openCodeSheet(token, () => void renderPartnerReport(root, token)));
    }
    if (list) mount(list, partnerValidationsBlock(v) || html``);
  });
  for (const b of $$<HTMLButtonElement>(root, '[data-ask]')) {
    b.addEventListener('click', async () => {
      b.disabled = true;
      try { await partnerAnswerAsk(token, b.dataset.ask!); await renderPartnerReport(root, token); }
      catch { b.disabled = false; b.textContent = 'Déjà pris ou indisponible'; }
    });
  }
}
